import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DashboardFeatureId } from '../../../AdviserArea/AdviserArea.types';
import { OptionType } from '../../../CommonComponents/OPL/Select/BaseSelect/types';
import { isOrionClientSelector } from '../../../CommonSelectors/CommonAreaSelector';
import { LandingPage } from '../../ClientAccess.types';
import {
  getLandingPageRequiredAccess,
  landingPageNameMap,
  MOBILE_LANDING_PAGES,
} from './LandingPage.meta';

const isMobileLandingPage = (landingPage: LandingPage, isOrionClient: boolean) => {
  switch (landingPage) {
    case LandingPage.Reports:
      return isOrionClient;
    default:
      return MOBILE_LANDING_PAGES.includes(landingPage);
  }
};

interface GetIsLandingPageValidParams {
  clientLandingPage: LandingPage | null;
  dashboardAccess: DashboardFeatureId[] | null;
  creditMonitoringAccess: boolean;
  landingPageOptions: OptionType<LandingPage>[];
  hasOrionReportsEnabled?: boolean;
  isOrionClient?: boolean;
}

export const getIsLandingPageValid = ({
  clientLandingPage,
  dashboardAccess,
  creditMonitoringAccess,
  landingPageOptions,
  hasOrionReportsEnabled,
  isOrionClient,
}: GetIsLandingPageValidParams) => {
  if (clientLandingPage === null) {
    return true;
  }

  if (!landingPageOptions.find(({ value }) => value === clientLandingPage)) {
    const requiredAccess = getLandingPageRequiredAccess({ isOrionClient })[clientLandingPage];

    if (requiredAccess && dashboardAccess) {
      return requiredAccess.every(featureId => (dashboardAccess || []).includes(featureId));
    }

    return false;
  }

  if (clientLandingPage === LandingPage.CreditMonitoring) {
    return creditMonitoringAccess;
  }

  const requiredAccess = getLandingPageRequiredAccess({ isOrionClient })[clientLandingPage];

  if (requiredAccess && dashboardAccess) {
    const access = requiredAccess.every(featureId => dashboardAccess.includes(featureId));

    if (clientLandingPage === LandingPage.Reports) {
      return access || hasOrionReportsEnabled;
    }

    return access;
  }

  return true;
};

interface GetIsTogglingDashboard {
  oldDashboardAccess: DashboardFeatureId[] | null;
  newDashBoardAccess?: DashboardFeatureId[] | null;
}

export const getIsTogglingDashboard = ({
  oldDashboardAccess,
  newDashBoardAccess,
}: GetIsTogglingDashboard) => {
  if (newDashBoardAccess === undefined) {
    return false;
  }

  return (
    ((newDashBoardAccess || []).length === 0 && (oldDashboardAccess || []).length > 0) ||
    ((newDashBoardAccess || []).length > 0 && (oldDashboardAccess || []).length === 0)
  );
};

interface HasNewsfeedOpSetOnOrionConnectParams {
  clientLandingPageOptions: OptionType<LandingPage>[];
}

const hasNewsfeedSetOnOrionConnect = ({
  clientLandingPageOptions,
}: HasNewsfeedOpSetOnOrionConnectParams) => {
  return (
    clientLandingPageOptions.filter(({ value }) => value === LandingPage.Newsfeed).length !== 0
  );
};

interface LandingPageParams {
  defaultClientLandingPage: LandingPage;
  clientLandingPageOptions: OptionType<LandingPage>[];
  dashboardAccess: DashboardFeatureId[] | null;
  creditMonitoringAccess: boolean;
  landingPageOptions: OptionType<LandingPage>[];
  hasOrionReportsEnabled?: boolean;
}

export const useLandingPage = ({
  defaultClientLandingPage,
  clientLandingPageOptions,
  dashboardAccess,
  creditMonitoringAccess,
  landingPageOptions,
  hasOrionReportsEnabled,
}: LandingPageParams) => {
  const isOrionClient = useSelector(isOrionClientSelector);

  const defaultLandingPageName = landingPageNameMap[defaultClientLandingPage];

  const userHasAccessToNewsfeed = hasNewsfeedSetOnOrionConnect({ clientLandingPageOptions });

  const filteredOptions = useMemo(() => {
    const options = clientLandingPageOptions.filter(({ value }) =>
      getIsLandingPageValid({
        clientLandingPage: value,
        dashboardAccess,
        creditMonitoringAccess,
        landingPageOptions,
        hasOrionReportsEnabled,
        isOrionClient,
      }),
    );

    const [webOptions, mobileOptions] = options.reduce(
      ([_webOptions, _mobileOptions], option) => {
        if (option.value !== null && isMobileLandingPage(option.value, isOrionClient)) {
          return [_webOptions, [..._mobileOptions, option]];
        }

        return [[..._webOptions, option], _mobileOptions];
      },
      [[], []] as OptionType<LandingPage | null>[][],
    );

    (MOBILE_LANDING_PAGES.includes(defaultClientLandingPage, isOrionClient)
      ? mobileOptions
      : webOptions
    ).push({
      value: null,
      label: `Use firm setting - ${defaultLandingPageName}`,
    });

    return [
      ...(mobileOptions.length > 0
        ? [{ label: 'Available in web and mobile app', options: mobileOptions }]
        : []),
      ...(webOptions.length > 0 ? [{ label: 'Available in web only', options: webOptions }] : []),
    ];
  }, [
    clientLandingPageOptions,
    creditMonitoringAccess,
    dashboardAccess,
    defaultClientLandingPage,
    defaultLandingPageName,
    hasOrionReportsEnabled,
    isOrionClient,
    landingPageOptions,
  ]);

  const isDefaultLandingPageValid = () => {
    if (defaultClientLandingPage === LandingPage.Newsfeed && !userHasAccessToNewsfeed) {
      return false;
    }

    return getIsLandingPageValid({
      clientLandingPage: defaultClientLandingPage,
      dashboardAccess,
      creditMonitoringAccess,
      landingPageOptions,
      hasOrionReportsEnabled,
      isOrionClient,
    });
  };

  return {
    defaultLandingPageName,
    filteredOptions,
    isDefaultLandingPageValid: isDefaultLandingPageValid(),
  };
};
